/**
 * Attribution storage utilities
 * Handles localStorage persistence with expiration
 */

import { ATTRIBUTION_STORAGE_KEY, ATTRIBUTION_WINDOW_DAYS } from "./constants";
import {
  captureAttributionData,
  shouldUpdateLastTouch,
  captureMetaCookies,
  parseDripClickParams,
} from "./capture";
import type {
  AttributionData,
  StoredAttribution,
  AttributionPayload,
} from "./types";

/**
 * Check if we're in a browser environment
 */
function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

/**
 * Calculate expiration date
 */
function getExpirationDate(): string {
  const date = new Date();
  date.setDate(date.getDate() + ATTRIBUTION_WINDOW_DAYS);
  return date.toISOString();
}

/**
 * Check if stored attribution has expired
 */
function isExpired(storedData: StoredAttribution): boolean {
  return new Date(storedData.expiresAt) < new Date();
}

/**
 * Get stored attribution from localStorage
 */
export function getStoredAttribution(): StoredAttribution | null {
  if (!isBrowser()) return null;

  try {
    const stored = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored) as StoredAttribution;

    // Check expiration
    if (isExpired(data)) {
      localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return null;
    }

    return data;
  } catch {
    // If parsing fails, remove corrupted data
    localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
    return null;
  }
}

/**
 * Save attribution to localStorage
 */
export function saveStoredAttribution(data: StoredAttribution): void {
  if (!isBrowser()) return;

  try {
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    // localStorage might be full or disabled
    console.warn("Failed to save attribution data:", e);
  }
}

/**
 * Clear stored attribution
 */
export function clearStoredAttribution(): void {
  if (!isBrowser()) return;
  localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
}

/**
 * Capture and store attribution from current page
 * This is the main function to call on page load
 */
export function captureAndStoreAttribution(): StoredAttribution | null {
  if (!isBrowser()) return null;

  const url = window.location.href;
  const referrer = document.referrer;

  // Capture current attribution data
  const currentAttribution = captureAttributionData(url, referrer);
  const dripClick = parseDripClickParams(new URL(url).searchParams);
  const dripMetadata =
    dripClick.dripEmailId && dripClick.dripStep
      ? {
          dripEmailId: dripClick.dripEmailId,
          dripStep: dripClick.dripStep,
          dripClickedAt: currentAttribution.capturedAt,
        }
      : {};

  // Capture Meta (Facebook) cookies for Conversions API
  const metaCookies = captureMetaCookies();

  // Get existing stored attribution
  const existingAttribution = getStoredAttribution();

  if (!existingAttribution) {
    // First visit - set both first and last touch
    const newData: StoredAttribution = {
      firstTouch: currentAttribution,
      lastTouch: currentAttribution,
      expiresAt: getExpirationDate(),
      fbp: metaCookies.fbp,
      fbc: metaCookies.fbc,
      ...dripMetadata,
    };
    saveStoredAttribution(newData);
    return newData;
  }

  // Always update Meta cookies if we have new values (they may be set after initial page load)
  const updatedMetaCookies = {
    fbp: metaCookies.fbp || existingAttribution.fbp,
    fbc: metaCookies.fbc || existingAttribution.fbc,
  };

  // Returning visitor - update last touch if we have new attribution data
  if (
    shouldUpdateLastTouch(currentAttribution, existingAttribution.lastTouch)
  ) {
    const updatedData: StoredAttribution = {
      ...existingAttribution,
      lastTouch: currentAttribution,
      // Extend expiration on new meaningful visits
      expiresAt: getExpirationDate(),
      ...updatedMetaCookies,
      ...dripMetadata,
    };
    saveStoredAttribution(updatedData);
    return updatedData;
  }

  // Even if last touch doesn't update, update Meta cookies if they've changed
  if (
    updatedMetaCookies.fbp !== existingAttribution.fbp ||
    updatedMetaCookies.fbc !== existingAttribution.fbc
  ) {
    const updatedData: StoredAttribution = {
      ...existingAttribution,
      ...updatedMetaCookies,
      ...dripMetadata,
    };
    saveStoredAttribution(updatedData);
    return updatedData;
  }

  return existingAttribution;
}

/**
 * Convert stored attribution to the payload format for server action
 */
export function getAttributionPayload(): AttributionPayload | null {
  const stored = getStoredAttribution();
  if (!stored) return null;

  return {
    firstTouchSource: stored.firstTouch.source,
    firstTouchMedium: stored.firstTouch.medium,
    firstTouchCampaign: stored.firstTouch.campaign,
    firstTouchContent: stored.firstTouch.content,
    firstTouchTerm: stored.firstTouch.term,
    firstTouchReferrer: stored.firstTouch.referrer,
    firstTouchLandingPage: stored.firstTouch.landingPage,
    firstTouchAt: stored.firstTouch.capturedAt,
    lastTouchSource: stored.lastTouch.source,
    lastTouchMedium: stored.lastTouch.medium,
    lastTouchCampaign: stored.lastTouch.campaign,
    lastTouchContent: stored.lastTouch.content,
    lastTouchTerm: stored.lastTouch.term,
    lastTouchReferrer: stored.lastTouch.referrer,
    lastTouchLandingPage: stored.lastTouch.landingPage,
    lastTouchAt: stored.lastTouch.capturedAt,
    // Meta (Facebook) tracking cookies
    fbp: stored.fbp,
    fbc: stored.fbc,
    dripEmailId: stored.dripEmailId ?? null,
    dripStep: stored.dripStep ?? null,
    dripClickedAt: stored.dripClickedAt ?? null,
  };
}

/**
 * Get Meta cookies for immediate use (e.g., for CAPI calls)
 * This captures fresh cookies from the browser
 */
export function getMetaCookies(): { fbp?: string; fbc?: string } {
  // First try to get from stored attribution
  const stored = getStoredAttribution();
  if (stored?.fbp || stored?.fbc) {
    return { fbp: stored.fbp, fbc: stored.fbc };
  }

  // Fall back to capturing fresh cookies
  return captureMetaCookies();
}

/**
 * Get a simplified attribution object for Stripe metadata
 * Stripe has limits on metadata values, so we keep it concise
 */
export function getAttributionForStripe(): Record<string, string> {
  const stored = getStoredAttribution();
  if (!stored) return {};

  const result: Record<string, string> = {};

  // First touch (most important for attribution)
  if (stored.firstTouch.source)
    result.ft_source = stored.firstTouch.source.slice(0, 50);
  if (stored.firstTouch.medium)
    result.ft_medium = stored.firstTouch.medium.slice(0, 50);
  if (stored.firstTouch.campaign)
    result.ft_campaign = stored.firstTouch.campaign.slice(0, 50);

  // Last touch
  if (stored.lastTouch.source)
    result.lt_source = stored.lastTouch.source.slice(0, 50);
  if (stored.lastTouch.medium)
    result.lt_medium = stored.lastTouch.medium.slice(0, 50);
  if (stored.lastTouch.campaign)
    result.lt_campaign = stored.lastTouch.campaign.slice(0, 50);

  return result;
}

/**
 * Get attribution data for PostHog event properties
 */
export function getAttributionForPostHog(): Record<string, string | null> {
  const stored = getStoredAttribution();
  if (!stored) return {};

  return {
    $initial_utm_source: stored.firstTouch.source,
    $initial_utm_medium: stored.firstTouch.medium,
    $initial_utm_campaign: stored.firstTouch.campaign,
    $initial_utm_content: stored.firstTouch.content,
    $initial_utm_term: stored.firstTouch.term,
    $initial_referrer: stored.firstTouch.referrer,
    $initial_landing_page: stored.firstTouch.landingPage,
    last_touch_source: stored.lastTouch.source,
    last_touch_medium: stored.lastTouch.medium,
    last_touch_campaign: stored.lastTouch.campaign,
  };
}
